import { useState } from 'react';
import { motion } from 'framer-motion';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { toast } from 'react-hot-toast';

export default function AddSubtopicModal({ isOpen, onClose, skill }) {
  const [formData, setFormData] = useState({
    name: '',
    status: 'not_started',
    priority: 'medium',
    estimatedHours: '',
    description: '',
    resources: []
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!skill?.id) {
      toast.error('Cannot add subtopic: Invalid skill');
      return;
    }

    try {
      const skillRef = doc(db, 'skills', skill.id);
      
      // Get current skill data
      const skillDoc = await getDoc(skillRef);
      if (!skillDoc.exists()) {
        toast.error('Skill not found');
        return;
      }

      const currentSkillData = skillDoc.data();
      const newSubtopic = {
        id: Date.now().toString(),
        ...formData,
        progress: 0,
        createdAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        resources: formData.resources.filter(resource => resource.title && resource.url),
      };

      // Add new subtopic to the array
      const updatedSubtopics = [...(currentSkillData.subtopics || []), newSubtopic];

      // Update the skill document
      await updateDoc(skillRef, {
        subtopics: updatedSubtopics
      });

      toast.success('Subtopic added successfully');
      onClose();
    } catch (error) {
      console.error('Error adding subtopic:', error);
      toast.error('Failed to add subtopic');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle resource changes
  const handleResourceChange = (index, e) => {
    const { name, value } = e.target;
    const newResources = [...formData.resources];
    newResources[index] = {
      ...newResources[index],
      [name]: value
    };
    setFormData(prev => ({
      ...prev,
      resources: newResources
    }));
  };

  // Add a new resource field
  const handleAddResource = () => {
    setFormData(prev => ({
      ...prev,
      resources: [...prev.resources, { title: '', url: '', type: 'link', description: '' }]
    }));
  };

  // Remove a resource field
  const handleRemoveResource = (index) => {
    setFormData(prev => ({
      ...prev,
      resources: prev.resources.filter((_, i) => i !== index)
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>
      
      <div className="flex min-h-full items-center justify-center p-4 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-lg transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 text-left shadow-xl transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Add New Subtopic
              </h3>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
            <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="input-field mt-1"
                  placeholder="Enter subtopic name"
                />
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="input-field mt-1"
                >
                  <option value="not_started">Not Started</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              <div>
                <label htmlFor="priority" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Priority
                </label>
                <select
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="input-field mt-1"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div>
                <label htmlFor="estimatedHours" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Estimated Hours
                </label>
                <input
                  type="number"
                  id="estimatedHours"
                  name="estimatedHours"
                  value={formData.estimatedHours}
                  onChange={handleChange}
                  min="0"
                  step="0.5"
                  className="input-field mt-1"
                  placeholder="Enter estimated hours"
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="input-field mt-1"
                  placeholder="Enter subtopic description"
                />
              </div>

            {/* Learning Resources Section */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Learning Resources</h4>
                <button
                  type="button"
                  onClick={handleAddResource}
                  className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                >
                  + Add Resource
                </button>
              </div>
              <div className="space-y-3">
                {formData.resources.map((resource, index) => (
                  <div key={index} className="flex items-start space-x-3 bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                    <div className="flex-grow space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Title</label>
                        <input
                          type="text"
                          name="title"
                          value={resource.title}
                          onChange={(e) => handleResourceChange(index, e)}
                          className="input-field text-sm mt-1"
                          placeholder="Resource title"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">URL</label>
                        <input
                          type="url"
                          name="url"
                          value={resource.url}
                          onChange={(e) => handleResourceChange(index, e)}
                          className="input-field text-sm mt-1"
                          placeholder="Resource URL"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Type</label>
                        <select
                          name="type"
                          value={resource.type}
                          onChange={(e) => handleResourceChange(index, e)}
                          className="input-field text-sm mt-1"
                        >
                          <option value="link">Link</option>
                          <option value="video">Video</option>
                          <option value="article">Article</option>
                          <option value="course">Course</option>
                        </select>
                      </div>
                       <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Description (Optional)</label>
                        <textarea
                          name="description"
                          value={resource.description}
                          onChange={(e) => handleResourceChange(index, e)}
                          rows="1"
                          className="input-field text-sm mt-1"
                          placeholder="Brief description"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveResource(index)}
                      className="p-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200 self-center"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                Add Subtopic
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
} 